import type { FC, ReactNode } from 'react'

import { Fragment } from 'react'
import { cn } from '@/lib/utils'
import { useWordPanel } from '@/prakrit/components/common/word-panel'

interface PrakritWordProps {
    word: string
    className?: string
    /** Highlight this word (e.g. the ending being taught). */
    highlight?: boolean
    /** Plain text (no Word Explorer), e.g. inside an answer tile. */
    plain?: boolean
}

/** One tappable Prakrit word: opens the Word Explorer with its full analysis. */
export const PrakritWord: FC<PrakritWordProps> = ({ word, className, highlight = false, plain = false }): ReactNode => {
    const { open } = useWordPanel()

    if (plain) {
        return <span lang='pra' className={cn(highlight && 'text-terracotta', className)}>{word}</span>
    }

    return (
        <button
            type='button'
            lang='pra'
            onClick={() => open(word)}
            className={cn(
                'inline rounded-sm px-0.5 -mx-0.5 underline decoration-dotted decoration-from-font underline-offset-[0.28em] decoration-current/35',
                'hover:bg-saffron/10 hover:decoration-saffron focus-visible:bg-saffron/10',
                highlight && 'text-terracotta',
                className
            )}
        >
            {word}
        </button>
    )
}

const SPLIT = /(\s+|[।॥?!,;:“”"‘’()—–]+)/

interface PrakritTextProps {
    text: string
    className?: string
    highlight?: string[]
    plain?: boolean
}

/** A Prakrit sentence with every word tappable. Punctuation stays as plain text. */
export const PrakritText: FC<PrakritTextProps> = ({ text, className, highlight = [], plain = false }): ReactNode => {
    const parts = text.normalize('NFC').split(SPLIT).filter(p => p !== '')

    return (
        <span lang='pra' className={className}>
            {parts.map((part, i) => {
                if (SPLIT.test(part) || !/[ऀ-ॿ]/.test(part)) return <Fragment key={i}>{part}</Fragment>

                return <PrakritWord key={i} word={part} plain={plain} highlight={highlight.includes(part)} />
            })}
        </span>
    )
}
